import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import moment from 'moment';
import {
  Typography,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Button,
  Grid,
  Box,
  Paper,
  CircularProgress,
  Container,
  Card,
  CardContent
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { InsertDriveFile, CheckCircle, Schedule, OpenInNew } from '@mui/icons-material';
import { assignmentService } from '../../services/api';

// Styled components using MUI v5 styled API
const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  borderRadius: 8,
}));

const SectionContainer = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const SummaryItem = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
  height: '100%',
}));

const ButtonContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: theme.spacing(3),
  '& > button:not(:first-of-type)': {
    marginLeft: theme.spacing(2),
  },
}));

const LoadingContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '50vh',
  '& > p': {
    marginTop: theme.spacing(2),
  },
}));

const AssignmentPreview = () => {
  const { assignment_id, courseId, ccrId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [assignment, setAssignment] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [error, setError] = useState(null);

  // Check if this is an independent view (no layout)
  const isIndependentView = location.search.includes('independent=true');

  useEffect(() => {
    const fetchAssignmentData = async () => {
      try {
        setLoading(true);
        const response = await assignmentService.getAssignmentPreview(assignment_id, courseId, ccrId);
        setAssignment(response.data.assignment);
        setSubmission(response.data.submissionRecord);
      } catch (err) {
        console.error('Error fetching assignment preview:', err);
        setError('Failed to load assignment preview');
      } finally {
        setLoading(false);
      }
    };

    fetchAssignmentData();
  }, [assignment_id, courseId, ccrId]);
  const handleViewSubmission = () => {
    navigate(`/assignments/submit/${assignment_id}/${courseId}/${ccrId}`);
  };

  const openIndependentView = () => {
    const url = `/assignments/preview/${assignment_id}?independent=true`;
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <LoadingContainer>
        <CircularProgress />
        <Typography variant="body1">Loading assignment preview...</Typography>
      </LoadingContainer>
    );
  }

  if (error || !assignment) {
    return (
      <Container sx={{ py: 4 }}>
        <Typography variant="h6" color="error" align="center">
          {error || 'Assignment not found'}
        </Typography>
      </Container>
    );
  }

  // Content for the assignment preview
  const assignmentContent = (
    <StyledCard>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4">
            {assignment.name}
          </Typography>
          <ButtonContainer>
            <Button
              variant="contained"
              color="primary"
              onClick={handleViewSubmission}
            >
              {submission ? 'View Submission' : 'Submit Assignment'}
            </Button>
          </ButtonContainer>
        </Box>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          {/* Left Column - Description */}
          <Grid item xs={12} md={7}>
            <SectionContainer>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Description
              </Typography>
              <Box
                sx={{ mt: 2, lineHeight: 1.6 }}
                dangerouslySetInnerHTML={{ __html: assignment.description }}
              />
            </SectionContainer>
          </Grid>

          {/* Right Column - Attachments and Summary */}
          <Grid item xs={12} md={5}>
            <SectionContainer>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Submission Summary
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <SummaryItem elevation={0}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle2">Points:</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>{assignment.points || 0} pts</Typography>
                    </Box>
                  </SummaryItem>
                </Grid>
                <Grid item xs={12}>
                  <SummaryItem elevation={0}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle2">Submitted Date:</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {submission ? moment(submission.submission_date).format('MMM DD, YYYY, h:mm A') : 'Not submitted yet'}
                      </Typography>
                    </Box>
                  </SummaryItem>
                </Grid>
                <Grid item xs={12}>
                  <SummaryItem elevation={0}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle2">Grade Status:</Typography>
                      <Box>
                        {!submission ? (
                          <Chip label="Not Submitted" size="small" />
                        ) : submission.grade_status === 1 ? (
                          <Chip
                            icon={<CheckCircle fontSize="small" />}
                            label="Graded"
                            color="primary"
                            size="small"
                          />
                        ) : (
                          <Chip
                            icon={<Schedule fontSize="small" />}
                            label="Pending"
                            size="small"
                          />
                        )}
                      </Box>
                    </Box>
                  </SummaryItem>
                </Grid>
              </Grid>

              {submission && (
                <Box sx={{ mt: 3 }}>
                  <Divider />
                  <Box sx={{
                    mt: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 2,
                    py: 1,
                    bgcolor: submission.grade_status === 1 ? 'rgba(76, 175, 80, 0.08)' : 'rgba(255, 152, 0, 0.08)',
                    borderRadius: 1
                  }}>
                    <Typography variant="subtitle1" component="span" sx={{ fontWeight: 600 }}>
                      Your Score:
                    </Typography>
                    <Typography
                      variant="body1"
                      component="span"
                      sx={{
                        fontWeight: 600,
                        color: submission.grade_status === 1 ? 'success.main' : 'warning.main'
                      }}
                    >
                      {submission.grade_status === 1 ? `${submission.marks || 0}/100` : 'Pending'}
                    </Typography>
                  </Box>
                </Box>
              )}
            </SectionContainer>

            {assignment.attachments && assignment.attachments.length > 0 && (
              <SectionContainer>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Attachments
                </Typography>
                <List>
                  {assignment.attachments.map((attachment, index) => {
                    // Construct the full URL path to the attachment
                    const attachmentUrl = `/uploads/assignments/${attachment.url}`;

                    return (
                      <ListItem key={index} button component="a" href={attachmentUrl} target="_blank" rel="noopener noreferrer">
                        <ListItemIcon>
                          <InsertDriveFile />
                        </ListItemIcon>
                        <ListItemText primary={attachment.name || 'Attachment'} />
                      </ListItem>
                    );
                  })}
                </List>

              </SectionContainer>
            )}


          </Grid>
        </Grid>
      </CardContent>
    </StyledCard>
  );

  // If this is an independent view, render without the main layout
  if (isIndependentView) {
    return (
      <Box sx={{
        p: 3,
        minHeight: '100vh',
        bgcolor: '#f5f5f5'
      }}>
        <Container maxWidth="lg">
          {assignmentContent}
        </Container>
      </Box>
    );
  }

  // Otherwise, render normally (will be wrapped in the main layout)
  return (
    <Container sx={{ py: 4 }}>
      {assignmentContent}
    </Container>
  );
};

export default AssignmentPreview;